import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateSignupForm } from "../utils/formValidation";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import BusinessTypePicker from "../components/BusinessTypePicker";
import {
  AUTH_FIELD_BG,
  AuthFieldLabel,
  AuthLink,
  AuthPage,
  AuthStagger,
  authSubmitSx,
} from "../components/auth/AuthLayout";
import CustomTextField from "../components/CustomTextField";
import CountryCodePhoneField from "../components/CountryCodePhoneField";
import { login, requestSignupCode, signup } from "../services/authService";
import {
  BUSINESS_THEMES,
  BUSINESS_TYPE_OPTIONS,
  BusinessTypeKey,
  IS_CLINIC,
  applyBusinessType,
} from "../platform/config";
import { useUser } from "../contexts/UserContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const RESEND_AFTER_SECONDS = 30;

// The self-serve build asks what kind of business it is, then the details,
// then a WhatsApp code; the sports build keeps its single form.
type Step = "type" | "details" | "code";

const apiMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [fullName, setFullName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [organization_name, setOrganization_name] = useState("");
  const [time_zone, setTime_zone] = useState(""); 
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState<Step>(IS_CLINIC ? "type" : "details");
  const [businessType, setBusinessType] = useState<BusinessTypeKey | null>(null);
  const [verification, setVerification] = useState<{ id: string; sentTo: string } | null>(null);
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);
  const [errors, setErrors] = useState<{
    fullName?: string;
    email?: string;
    phone?: string;
    password?: string;
    organization_name?: string;
    time_zone?: string;
    confirmPassword?: string;
  }>({});

  const validate = () => {
    const validationResults = validateSignupForm({
      fullName,
      email,
      phone: phoneNumber,
      password,
      confirmPassword,
    });
    const newErrors: { [key: string]: string } = {};
    Object.entries(validationResults).forEach(([key, result]) => {
      if (!result.valid && result.error) {
        newErrors[key] = result.error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Self-serve, after the details: a WhatsApp code to the owner's number.
  const sendCode = async () => {
    setLoading(true);
    try {
      const sent = await requestSignupCode(phoneNumber, countryCode);
      setVerification({ id: sent.verificationId, sentTo: sent.sentTo });
      setCode("");
      setResendIn(RESEND_AFTER_SECONDS);
      setStep("code");
    } catch (err) {
      toast.error(apiMessage(err, "Could not send the WhatsApp code."), { position: "top-right" });
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === "details" && !validate()) return;
    if (IS_CLINIC && step === "details") {
      await sendCode();
      return;
    }
    setLoading(true);

    try {
      const result = await signup({
        fullName,
        email,
        password,
        organizationName: organization_name,
        country: countryCode,
        phone: phoneNumber,
        ...(IS_CLINIC && {
          businessType: businessType ?? "NEUTRAL",
          verificationId: verification?.id,
          verificationCode: code.trim(),
        }),
      });

      // Self-serve signups are approved on the spot: sign straight in and open
      // the new branch, with the free trial already running.
      if (result.success && result.autoApproved) {
        const session = await login(email.trim(), password);
        if (session.success && session.mode === "ORG_LOGIN") {
          await refreshUser();
          const home = result.businessId ? `/dashboard/${result.businessId}` : "/home";
          // Reloads into the business type's words and colours.
          if (applyBusinessType(session.user.businessType, home)) return;
          toast.success("Welcome! Your free trial has started.", { position: "top-right" });
          navigate(home, { replace: true });
          return;
        }
        // Signed up but the automatic sign-in failed: the login page works.
        toast.success("Your account is ready. Please sign in.", { position: "top-right" });
        navigate("/login", { replace: true });
        return;
      }

      if (result.success) {
        const successMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof (result as any)?.user?.message === "string" &&
            (result as any).user.message.trim()) ||
          (typeof (result as any)?.user?.msg === "string" &&
            (result as any).user.msg.trim()) ||
          "Account created successfully!";
        toast.success(successMessage, {
          position: "top-right",
        });
        navigate("/home");
      } else {
        const errorMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof result.error === "string" && result.error.trim()) ||
          result.error ||
          "Signup failed";
        toast.error(errorMessage, {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Signup error:", error);
      const message =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error ||
        (error as any)?.message ||
        "An unexpected error occurred";
      toast.error(message, {
        position: "top-right",
        toastId: String(message),
      });
    } finally {
      setLoading(false);
    }
  };

  function onCountryCodeChange(value: string): void {
    setCountryCode(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  function onPhoneNumberChange(value: string): void {
    setPhoneNumber(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  const passwordToggle = (
    shown: boolean,
    toggle: () => void,
    label: string
  ) => (
    <IconButton
      aria-label={shown ? `Hide ${label}` : `Show ${label}`}
      onClick={toggle}
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
    >
      {shown ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  );

  const footer = (
    <>
      Already have an account? <AuthLink to="/login">Log in</AuthLink>
    </>
  );

  if (step === "type") {
    return (
      <AuthPage
        title="What kind of business do you run?"
        subtitle="We set up the words, colours and starting lists for it. You can change it later in Settings."
        footer={footer}
      >
        <AuthStagger startMs={120} sx={{ width: "100%" }}>
          <BusinessTypePicker value={businessType} onChange={setBusinessType} />
          <Button
            variant="contained"
            fullWidth
            disabled={!businessType}
            onClick={() => setStep("details")}
            sx={{ ...authSubmitSx, mt: 2.5 }}
          >
            Continue
          </Button>
        </AuthStagger>
      </AuthPage>
    );
  }

  if (step === "code" && verification) {
    return (
      <AuthPage
        title="Check WhatsApp"
        subtitle={`We sent a 6-digit code to ${verification.sentTo}. Enter it to create your account.`}
        footer={footer}
      >
        <AuthStagger component="form" startMs={120} onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <AuthFieldLabel>Code</AuthFieldLabel>
          <CustomTextField
            label=""
            value={code}
            bgColor={AUTH_FIELD_BG}
            placeholder="123456"
            autoFocus
            autoComplete="one-time-code"
            inputProps={{ inputMode: "numeric", maxLength: 6, "aria-label": "WhatsApp code", style: { letterSpacing: "0.35em", fontSize: "1.2rem" } }}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Button size="small" onClick={() => setStep("details")} disabled={loading} sx={{ textTransform: "none" }}>
              Change number
            </Button>
            <Button size="small" onClick={sendCode} disabled={loading || resendIn > 0} sx={{ textTransform: "none" }}>
              {resendIn > 0 ? `Send again in ${resendIn}s` : "Send the code again"}
            </Button>
          </Box>
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={loading || code.length < 6}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={authSubmitSx}
          >
            {loading ? "Creating account..." : "Create account"}
          </Button>
        </AuthStagger>
      </AuthPage>
    );
  }

  const chosen = BUSINESS_TYPE_OPTIONS.find((option) => option.key === businessType);

  return (
    <AuthPage
      title="Create your account"
      subtitle="Set up your organization in a minute."
      footer={footer}
    >
      <AuthStagger component="form" startMs={120} onSubmit={handleSubmit} sx={{ width: "100%" }}>
        {IS_CLINIC && chosen && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1, pl: 1.5, borderRadius: 2, bgcolor: BUSINESS_THEMES[chosen.key].tint }}>
            <Typography sx={{ fontSize: "0.88rem", flexGrow: 1 }}>
              Business type: <strong>{chosen.name}</strong>
            </Typography>
            <Button size="small" onClick={() => setStep("type")} sx={{ textTransform: "none" }}>
              Change
            </Button>
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
          }}
        >
          <Box>
            <AuthFieldLabel>Full Name</AuthFieldLabel>
            <CustomTextField
              label=""
              type="text"
              value={fullName}
              bgColor={AUTH_FIELD_BG}
              placeholder="Jane Doe"
              autoComplete="name"
              onChange={(e) => {
                setFullName(e.target.value);
                setErrors((prev) => ({ ...prev, fullName: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.fullName}
              helperText={errors.fullName}
            />
          </Box>
          <Box>
            <AuthFieldLabel>Organization Name</AuthFieldLabel>
            <CustomTextField
              label=""
              type="text"
              value={organization_name}
              bgColor={AUTH_FIELD_BG}
              placeholder={IS_CLINIC ? "Your business name" : "Your club or company"}
              autoComplete="organization"
              onChange={(e) => {
                setOrganization_name(e.target.value);
                setErrors((prev) => ({ ...prev, organization_name: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.organization_name}
              helperText={errors.organization_name}
            />
          </Box>
        </Box>
        <AuthFieldLabel>Email</AuthFieldLabel>
        <CustomTextField
          label=""
          type="email"
          value={email}
          bgColor={AUTH_FIELD_BG}
          placeholder="you@company.com"
          autoComplete="email"
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          required
          fullWidth
          sx={{ mb: 2 }}
          error={!!errors.email}
          helperText={errors.email}
        />
        <Box sx={{ mb: 2 }}>
          <CountryCodePhoneField
            countryCode={countryCode}
            phoneNumber={phoneNumber}
            onCountryCodeChange={onCountryCodeChange}
            onPhoneNumberChange={onPhoneNumberChange}
            error={!!errors.phone}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
          }}
        >
          <Box>
            <AuthFieldLabel>Password</AuthFieldLabel>
            <CustomTextField
              label=""
              type={showPassword ? "text" : "password"}
              value={password}
              bgColor={AUTH_FIELD_BG}
              autoComplete="new-password"
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.password}
              helperText={errors.password}
              endIcon={passwordToggle(
                showPassword,
                () => setShowPassword((prev) => !prev),
                "password"
              )}
            />
          </Box>
          <Box>
            <AuthFieldLabel>Confirm Password</AuthFieldLabel>
            <CustomTextField
              label=""
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              bgColor={AUTH_FIELD_BG}
              autoComplete="new-password"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              endIcon={passwordToggle(
                showConfirmPassword,
                () => setShowConfirmPassword((prev) => !prev),
                "confirm password"
              )}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : undefined
          }
          sx={{ ...authSubmitSx, mt: 1.5 }}
        >
          {IS_CLINIC
            ? loading
              ? "Sending WhatsApp code..."
              : "Continue"
            : loading
              ? "Creating account..."
              : "Create account"}
        </Button>
        {IS_CLINIC && (
          <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", textAlign: "center", mt: 1 }}>
            We send a code to this number on WhatsApp to confirm it.
          </Typography>
        )}
      </AuthStagger>
    </AuthPage>
  );
};

export default Signup;
